import { useEffect, useCallback, useState } from 'react';
import { AnnotationsForGrid } from '../Annotation/annotation.types';

export type ContextMenuConfig = {
  show: boolean;
  left: number;
  top: number;
  names: string[];
  unsaved: boolean;
};

function useContextMenu(
  canvasEl: Nullable<fabric.Canvas>,
  removeFindings: boolean,
  onFindingRemoved: (objects: string[]) => void,
  editFindings: boolean,
  onFindingEdited: (objects: string[]) => void,
  havePermissionToUse: boolean,
  onSaveFinding: (saveFinding: boolean) => void,
  saveFinding: boolean,
  presentAnnotations: AnnotationsForGrid[] | null,
  multiSelectObjects: string[]
) {
  const [saveProcessed, setSaveProcessed] = useState<boolean>(false);
  const [contextMenuConfig, setContextMenuConfig] = useState<ContextMenuConfig>({ show: false, left: 0, top: 0, names: [], unsaved: false });

  const onClick = useCallback(() => {
    setContextMenuConfig({ ...contextMenuConfig, show: false });
  }, [contextMenuConfig]);

  useEffect(() => {
    if (contextMenuConfig.unsaved && !saveProcessed) {
      setContextMenuConfig({
        ...contextMenuConfig,
        unsaved: false,
        show: true
      });
    }
  }, [contextMenuConfig, saveProcessed]);

  useEffect(() => {
    if (!saveFinding) {
      setSaveProcessed(false);
    }
  }, [saveFinding]);

  const onRightClick = useCallback(
    (e: MouseEvent) => {
      if (canvasEl!.getActiveObject() && havePermissionToUse) {
        e.preventDefault();

        let show = true;

        if (presentAnnotations && presentAnnotations.some(x => x.dirty)) {
          show = false;
          onSaveFinding(true);
          setSaveProcessed(true);
        }

        let config: ContextMenuConfig = {
          show,
          unsaved: !show,
          left: e.pageX,
          top: e.pageY,
          names: multiSelectObjects.length ? multiSelectObjects : canvasEl!.getActiveObjects().map(x => x.name || '')
        };
        setContextMenuConfig(config);
      }
    },
    [canvasEl, havePermissionToUse, multiSelectObjects, onSaveFinding, presentAnnotations]
  );

  const onRemoveFindings = useCallback(() => {
    const { names } = contextMenuConfig;
    onFindingRemoved(names);
  }, [contextMenuConfig, onFindingRemoved]);

  useEffect(() => {
    if (removeFindings) {
      onRemoveFindings();
    }
  }, [removeFindings, onRemoveFindings]);

  const onEditFindings = useCallback(() => {
    const { names } = contextMenuConfig;
    onFindingEdited(names);
  }, [contextMenuConfig, onFindingEdited]);

  useEffect(() => {
    if (editFindings) {
      onEditFindings();
    }
  }, [editFindings, onEditFindings]);

  useEffect(() => {
    window.addEventListener('contextmenu', onRightClick);
    window.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('contextmenu', onRightClick);
      window.removeEventListener('click', onClick);
    };
  }, [onClick, onRightClick]);

  return [contextMenuConfig];
}

export default useContextMenu;
